import { useState } from 'react';
import { motion } from 'framer-motion';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { songs } from '../../../mocks/songs';
import MusicPlayer from '../../../components/feature/MusicPlayer';

interface SongGalleryProps {
  language: 'de' | 'ru';
}

export default function SongGallery({ language }: SongGalleryProps) {
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [selectedSong, setSelectedSong] = useState<number | null>(null);
  const [playingSong, setPlayingSong] = useState<number | null>(null);
  const headerAnim = useScrollAnimation();
  const filtersAnim = useScrollAnimation();
  const cardsAnim = useScrollAnimation();

  const content = {
    de: {
      title: 'Lieder-Archiv',
      subtitle: 'Eine Sammlung von 60 Jahren musikalischer Reise',
      filters: {
        all: 'Alle',
        heroic: 'Ruf der Tat',
        philosophical: 'Stille Wasser',
        family: 'Familie',
        romantic: 'Romantik'
      },
      listen: 'Anhören',
      close: 'Schließen',
      lyrics: 'Verse',
      inspiration: 'Inspiration'
    },
    ru: {
      title: 'Архив песен',
      subtitle: 'Коллекция 60-летнего музыкального путешествия',
      filters: {
        all: 'Все',
        heroic: 'Зов действия',
        philosophical: 'Тихие воды',
        family: 'Семья',
        romantic: 'Романтика'
      },
      listen: 'Слушать',
      close: 'Закрыть',
      lyrics: 'Стихи',
      inspiration: 'Вдохновение'
    }
  };

  const t = content[language];

  const filteredSongs = activeFilter === 'all' 
    ? songs 
    : songs.filter(song => song.category === activeFilter);

  const handlePlaySong = (songId: number) => {
    setPlayingSong(songId);
  };

  const handleSelectSong = (songId: number) => {
    setPlayingSong(songId);
  };

  const handleNextSong = () => {
    if (playingSong !== null) {
      const currentIndex = songs.findIndex(s => s.id === playingSong);
      const nextIndex = (currentIndex + 1) % songs.length;
      setPlayingSong(songs[nextIndex].id);
    }
  };

  const handlePreviousSong = () => {
    if (playingSong !== null) {
      const currentIndex = songs.findIndex(s => s.id === playingSong);
      const previousIndex = currentIndex === 0 ? songs.length - 1 : currentIndex - 1;
      setPlayingSong(songs[previousIndex].id);
    }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const cardVariant = {
    hidden: { opacity: 0, y: 40, scale: 0.9 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.6, ease: 'easeOut' } }
  };

  return (
    <>
      <section id="songs" className="py-24 bg-gradient-to-b from-white to-[#F5F5DC]">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div 
            ref={headerAnim.ref}
            initial="hidden"
            animate={headerAnim.isVisible ? "visible" : "hidden"}
            variants={fadeInUp}
            className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12"
          >
            <h2 className="font-serif text-5xl md:text-6xl font-bold text-[#1e3a5f] mb-4 md:mb-0">
              {t.title}
            </h2>
            <p className="font-serif text-lg text-gray-600 max-w-md">
              {t.subtitle}
            </p>
          </motion.div>

          {/* Decorative Line */}
          <div className="h-px bg-gradient-to-r from-transparent via-[#C5B358] to-transparent mb-12"></div>

          {/* Filter Buttons */}
          <motion.div 
            ref={filtersAnim.ref}
            initial="hidden"
            animate={filtersAnim.isVisible ? "visible" : "hidden"}
            variants={fadeInUp}
            className="flex flex-wrap justify-center gap-4 mb-16"
          >
            {Object.entries(t.filters).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setActiveFilter(key)}
                className={`px-8 py-3 rounded-full font-serif text-base transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeFilter === key
                    ? 'bg-[#8b2332] text-white border-2 border-[#8b2332]'
                    : 'bg-transparent text-[#1e3a5f] border-2 border-[#C5B358] hover:border-[#8b2332]'
                }`}
              >
                {label}
              </button>
            ))}
          </motion.div>

          {/* Song Cards Grid */}
          <motion.div 
            ref={cardsAnim.ref}
            initial="hidden"
            animate={cardsAnim.isVisible ? "visible" : "hidden"}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {filteredSongs.map((song) => (
              <motion.div
                key={song.id}
                variants={cardVariant}
                className="bg-[#fefdfb] rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group"
              >
                {/* Cover Image */}
                <div 
                  className="relative w-full h-80 overflow-hidden cursor-pointer"
                  onClick={() => setSelectedSong(song.id)}
                >
                  <img
                    src={song.cover}
                    alt={song.title[language]}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#C5B358] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  
                  {/* Playing Indicator */}
                  {playingSong === song.id && (
                    <div className="absolute top-4 right-4 bg-[#8b2332] text-white px-3 py-1 rounded-full text-xs font-serif flex items-center gap-2">
                      <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                      Playing
                    </div>
                  )}
                </div>

                {/* Card Content */}
                <div className="p-6">
                  <h3 className="font-serif text-2xl font-bold text-[#1e3a5f] mb-3">
                    {song.title[language]}
                  </h3>
                  <p className="font-serif text-sm text-gray-600 mb-4 line-clamp-2">
                    {song.description[language]}
                  </p>
                  
                  {/* Verse Preview */}
                  <div className="mb-4 p-3 bg-[#F5F5DC]/50 rounded-lg border-l-2 border-[#C5B358]">
                    <p className="font-serif text-xs text-gray-700 italic line-clamp-2">
                      {song.verse[language]}
                    </p>
                  </div>

                  {/* Poet Inspiration */}
                  <div className="mb-6">
                    <div className="flex items-center text-[#C5B358]">
                      <i className="ri-quill-pen-line text-lg mr-2"></i>
                      <p className="font-serif text-xs italic">
                        {song.inspiration}
                      </p>
                    </div>
                  </div>

                  {/* Play Button */}
                  <div className="flex justify-center">
                    <button 
                      onClick={() => handlePlaySong(song.id)}
                      className="w-14 h-14 rounded-full bg-[#8b2332] border-2 border-[#C5B358] flex items-center justify-center hover:bg-[#6d1a27] transition-all duration-300 shadow-md hover:shadow-lg cursor-pointer group"
                    >
                      {playingSong === song.id ? (
                        <i className="ri-pause-fill text-2xl text-white group-hover:scale-110 transition-transform"></i>
                      ) : (
                        <i className="ri-play-fill text-2xl text-white group-hover:scale-110 transition-transform"></i>
                      )}
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Song Detail Modal */}
          {selectedSong !== null && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setSelectedSong(null)}
            >
              <motion.div 
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="bg-[#fefdfb] rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-8">
                  {/* Close Button */}
                  <div className="flex justify-end mb-4">
                    <button
                      onClick={() => setSelectedSong(null)}
                      className="w-10 h-10 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center cursor-pointer transition-colors"
                    >
                      <i className="ri-close-line text-2xl text-gray-700"></i>
                    </button>
                  </div>

                  {/* Song Details */}
                  {songs.find(s => s.id === selectedSong) && (
                    <div className="grid md:grid-cols-2 gap-8">
                      <div className="w-full h-96 rounded-lg overflow-hidden shadow-lg">
                        <img
                          src={songs.find(s => s.id === selectedSong)!.cover}
                          alt={songs.find(s => s.id === selectedSong)!.title[language]}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div>
                        <h3 className="font-serif text-4xl font-bold text-[#1e3a5f] mb-4">
                          {songs.find(s => s.id === selectedSong)!.title[language]}
                        </h3>
                        <p className="font-serif text-base text-gray-700 mb-6">
                          {songs.find(s => s.id === selectedSong)!.description[language]}
                        </p>
                        
                        <div className="mb-6">
                          <h4 className="font-serif text-lg font-semibold text-[#8b2332] mb-3 flex items-center">
                            <i className="ri-quill-pen-line text-xl mr-2"></i>
                            {t.lyrics}
                          </h4>
                          <div className="p-4 bg-[#F5F5DC]/50 rounded-lg border-l-4 border-[#C5B358]">
                            <p className="font-serif text-base text-gray-700 italic leading-relaxed">
                              {songs.find(s => s.id === selectedSong)!.verse[language]}
                            </p>
                          </div>
                        </div>

                        <div className="mb-6">
                          <h4 className="font-serif text-lg font-semibold text-[#8b2332] mb-3 flex items-center">
                            <i className="ri-book-open-line text-xl mr-2"></i>
                            {t.inspiration}
                          </h4>
                          <p className="font-serif text-base text-[#C5B358]">
                            {songs.find(s => s.id === selectedSong)!.inspiration}
                          </p>
                        </div>

                        <button 
                          onClick={() => {
                            handlePlaySong(selectedSong);
                            setSelectedSong(null);
                          }}
                          className="w-full px-8 py-4 bg-[#8b2332] text-white font-serif text-lg rounded-full border-2 border-[#C5B358] hover:bg-[#6d1a27] transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                        >
                          {t.listen}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </div>
      </section>

      {/* Music Player */}
      {playingSong !== null && (
        <MusicPlayer
          song={songs.find(s => s.id === playingSong) || null}
          language={language}
          onClose={() => setPlayingSong(null)}
          onNext={handleNextSong}
          onPrevious={handlePreviousSong}
          allSongs={songs}
          onSelectSong={handleSelectSong}
        />
      )}
    </>
  );
}
